import {Box, StylesProvider, useMultiStyleConfig, useTheme} from '@chakra-ui/react';
import {ChakraStylesConfig, Select} from 'chakra-react-select';
import React, {memo, useState} from 'react';
import {IconType} from 'react-icons';
import {components as dropdownComponent, Props as SelectProps} from 'react-select';
import {UiIcon} from '.';

export declare type Size = 'sm' | 'md' | 'lg';
export declare type TagVariant = 'subtle' | 'solid' | 'outline' | undefined;
export declare type SelectedOptionStyle = 'color' | 'check';
export type SizeProps = {
  sm: string | number;
  md: string | number;
  lg: string | number;
};

export interface UiDropdownProps extends SelectProps {
  size?: Size;
  includeAll?: boolean;
  colorScheme?: string;
  isInvalid?: boolean;
  tagVariant?: TagVariant;
  hasStickyGroupHeaders?: boolean;
  selectedOptionStyle?: SelectedOptionStyle;
  selectedOptionColor?: string;
  multiple?: boolean;
  onSelect?: (value?: string | number) => any;
  selectedFontWeight?: string;
  icon?: IconType;
  styleControl?: React.CSSProperties;
  customChakraStyles?: ChakraStylesConfig;
}

const DropdownIndicator = (props: any) => {
  return (
    dropdownComponent.DropdownIndicator && (
      <dropdownComponent.DropdownIndicator {...props}>
        <UiIcon as={props.icon} />
      </dropdownComponent.DropdownIndicator>
    )
  );
};

const Menu: (args: any) => any = ({children, w, innerProps, selectProps: {size}}) => {
  const menuStyles = useMultiStyleConfig('Menu', {});
  const chakraTheme = useTheme();
  const borderRadii: SizeProps = {
    sm: chakraTheme.radii.sm,
    md: chakraTheme.radii.md,
    lg: chakraTheme.radii.md,
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '100%',
        my: '8px',
        w: w,
        zIndex: 1,
        overflow: 'auto',
        rounded: borderRadii[size as Size],
      }}
      {...innerProps}
    >
      <StylesProvider value={menuStyles}>{children}</StylesProvider>
    </Box>
  );
};

export const UiDropdown = memo(
  React.forwardRef(function UiDropdown(
    {
      multiple = false,
      menuWidth = '100%',
      components = {},
      size = 'sm',
      selectedFontWeight,
      icon,
      styles,
      styleControl,
      customChakraStyles,
      ...rest
    }: UiDropdownProps,
    ref,
  ) {
    const chakraStyles = {
      singleValue: (provided: any) => {
        const fontWeight = selectedFontWeight ? selectedFontWeight : 'normal';
        return {...provided, fontWeight};
      },
      control: (base: any, state: any) => ({
        ...base,
        ...styleControl,
      }),
    };

    if (icon) {
      components = {
        ...components,
        DropdownIndicator: (props: any) => <DropdownIndicator icon={icon} {...props} />,
      };
    }

    return (
      <Select
        ref={ref}
        size={size}
        isMulti={multiple}
        components={{Menu: (props: any) => <Menu w={menuWidth} {...props} />, ...components}}
        minW="10em"
        chakraStyles={{...chakraStyles, ...customChakraStyles}}
        {...rest}
      />
    );
  }),
);
